import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";

const KINDS = [
  { id: "caption", label: "Social caption" },
  { id: "headline", label: "Headlines" },
  { id: "tagline", label: "Taglines" },
  { id: "description", label: "Description" },
  { id: "ideas", label: "Campaign ideas" },
];
const TONES = ["confident", "playful", "luxury", "minimal", "bold", "friendly"];

export default function Marketing() {
  const [kind, setKind] = useState("caption");
  const [tone, setTone] = useState("confident");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe what you need first"); return; }
    setBusy(true); setResult("");
    try {
      const { data } = await api.post("/ai/copy", { prompt, kind, tone });
      setResult(data.text);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-16 md:pt-10 max-w-4xl relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 560, height: 560, top: -140, left: "55%" }} /></div>
        <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">Creative thinking</div>
        <h1 className="text-3xl sm:text-4xl font-medium text-kann-text-pri mt-2">AI Marketing Studio</h1>
        <p className="font-light text-kann-text-sec mt-2">Generate on-brand copy, taglines and campaign ideas in your voice.</p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 space-y-5">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">What do you need?</label>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="kind-selector">
                {KINDS.map((k) => (
                  <button key={k.id} onClick={() => setKind(k.id)} data-testid={`kind-${k.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${kind === k.id ? "bg-kann-ink text-white font-semibold" : "border border-kann-border/40 text-kann-text-sec hover:bg-kann-surface-active"}`}>
                    {k.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Tone</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button key={t} onClick={() => setTone(t)}
                    className={`px-3 py-1.5 rounded-full text-sm capitalize transition-colors ${tone === t ? "bg-kann-surface-active text-kann-gold border border-kann-gold/50" : "border border-kann-border/40 text-kann-text-sec hover:bg-kann-surface-active"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Brief</label>
              <textarea data-testid="marketing-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
                placeholder="e.g. Weekend flash sale on our sneaker collection, 30% off, target Gen-Z"
                className="mt-3 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2.5 text-sm outline-none resize-none" />
            </div>
            <button onClick={generate} disabled={busy} data-testid="generate-copy-btn"
              className="w-full bg-kann-ink text-white font-semibold rounded-full py-3 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
            </button>
          </div>

          <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Result</span>
              {result && (
                <button onClick={copy} data-testid="copy-result-btn" className="text-xs text-kann-text-sec hover:text-kann-gold flex items-center gap-1">
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
                </button>
              )}
            </div>
            <div data-testid="marketing-result" className="flex-1 min-h-[240px] whitespace-pre-wrap text-kann-text-pri text-sm leading-relaxed">
              {busy ? <span className="text-kann-text-sec">Thinking…</span> : result || <span className="text-kann-text-sec">Your generated copy will appear here.</span>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
